import React, { useState } from 'react';
import { UserSettings, View } from '../types';
import { INITIAL_USER_SETTINGS, ROLE_BONUS_CONFIG, EDU_CONFIG } from '../config';
import Card from './common/Card';
import { UserCircleIcon, UserGroupIcon, AcademicCapIcon } from './common/Icons';
import { updateUserSettings } from '../services/apiService';
import { APP_MODE } from '../config';

const calculateAge = (dob: string): number => {
    if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) return -1;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
};

interface RoleSelectionProps {
    user: UserSettings;
    onUpdateSettings: (update: React.SetStateAction<UserSettings | null>) => void;
    onNavigate: (view: View) => void;
}

const RoleCard: React.FC<{ icon: React.ReactNode; title: string; description: string; onClick: () => void; }> = ({ icon, title, description, onClick }) => (
    <button onClick={onClick} className="w-full text-left p-6 border rounded-lg hover:bg-gray-50 hover:border-teal-500 transition-all focus:outline-none focus:ring-2 focus:ring-teal-500 group">
        <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                {icon}
            </div>
            <div>
                <h3 className="text-lg font-bold text-gray-800">{title}</h3>
                <p className="text-sm text-gray-500 mt-1">{description}</p>
            </div>
        </div>
    </button>
);

const RoleSelection: React.FC<RoleSelectionProps> = ({ user, onUpdateSettings, onNavigate }) => {
    const [step, setStep] = useState<'dob' | 'role'>(
        !user.dob || !user.dob.trim() ? 'dob' : 'role'
    );
    const [dob, setDob] = useState(user.dob || '');
    const [isSavingDob, setIsSavingDob] = useState(false);

    const age = dob ? calculateAge(dob) : -1;
    const ageThreshold = EDU_CONFIG?.STUDENT_DEFAULT_AGE_THRESHOLD ?? 18;
    const isUnderThreshold = age >= 0 && age < ageThreshold;

    const handleDobSubmit = async () => {
        if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
            alert('Vui lòng nhập ngày sinh hợp lệ (định dạng YYYY-MM-DD).');
            return;
        }
        const a = calculateAge(dob);
        if (a < 0 || a > 120) {
            alert('Ngày sinh không hợp lệ. Vui lòng kiểm tra lại.');
            return;
        }
        setIsSavingDob(true);
        try {
            const merged = { ...user, dob };
            onUpdateSettings(merged);
            if (APP_MODE === 'PRODUCTION') {
                await updateUserSettings(merged);
            }
            setStep('role');
        } catch (e) {
            console.error('Lỗi lưu ngày sinh:', e);
            alert('Không lưu được ngày sinh. Vui lòng thử lại.');
        } finally {
            setIsSavingDob(false);
        }
    };

    const handleSelectRole = (role: 'teacher' | 'parent' | 'student') => {
        const baseSettings = user || INITIAL_USER_SETTINGS;
        const updates: Partial<UserSettings> = { role };
        if (dob) updates.dob = dob;
        if (role === 'teacher') {
            updates.teacherName = baseSettings.teacherName || 'Giáo viên mới';
            updates.mainSubject = 'Khoa học';
            if (ROLE_BONUS_CONFIG.ENABLE_BONUS) {
                updates.totalCredits = (baseSettings.totalCredits || 0) + ROLE_BONUS_CONFIG.TEACHER;
            }
        } else if (role === 'parent') {
            updates.teacherName = baseSettings.teacherName || 'Phụ huynh';
            if (ROLE_BONUS_CONFIG.ENABLE_BONUS) {
                updates.totalCredits = (baseSettings.totalCredits || 0) + ROLE_BONUS_CONFIG.PARENT;
            }
        } else if (role === 'student') {
            updates.teacherName = baseSettings.teacherName || 'Học sinh';
            if (ROLE_BONUS_CONFIG.ENABLE_BONUS) {
                updates.totalCredits = (baseSettings.totalCredits || 0) + ROLE_BONUS_CONFIG.STUDENT;
            }
        }
        const newSettings = { ...baseSettings, ...updates };
        onUpdateSettings(newSettings);

        const dashboardMap: Record<string, View> = {
            teacher: View.TeacherTools,
            parent: View.ParentDashboard,
            student: View.StudentDashboard,
        };
        const targetView = dashboardMap[role] ?? View.Dashboard;
        // Defer navigation để React kịp cập nhật user.role trước khi route guard kiểm tra
        setTimeout(() => onNavigate(targetView), 0);
    };

    if (step === 'dob') {
        return (
            <div className="flex justify-center items-center py-16">
                <Card className="max-w-md w-full p-10">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900">Thông tin cơ bản</h2>
                        <p className="mt-3 text-gray-600">
                            Để cá nhân hóa trải nghiệm và gợi ý vai trò phù hợp, vui lòng cho biết ngày sinh của bạn.
                        </p>
                    </div>
                    <div className="mt-8">
                        <label className="block text-sm font-bold text-gray-700 mb-2">Ngày sinh</label>
                        <input
                            type="date"
                            value={dob}
                            onChange={e => setDob(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            max={new Date().toISOString().slice(0, 10)}
                        />
                    </div>
                    <div className="flex flex-col space-y-3 mt-6">
                        <button
                            onClick={handleDobSubmit}
                            disabled={!dob || isSavingDob}
                            className="w-full py-3 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
                        >
                            {isSavingDob ? 'Đang lưu...' : 'Tiếp tục'}
                        </button>
                        <button
                            onClick={() => setStep('role')}
                            className="w-full py-3 bg-gray-100 text-gray-600 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                        >
                            Bỏ qua
                        </button>
                    </div>
                </Card>
            </div>
        );
    }

    if (isUnderThreshold) {
        return (
            <div className="flex justify-center items-center py-16">
                <Card className="max-w-md w-full p-10">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900">Chào mừng bạn!</h2>
                        <p className="mt-3 text-gray-600">
                            Dựa trên ngày sinh của bạn ({age} tuổi), chúng tôi gợi ý vai trò <strong>Học sinh</strong> để bạn khám phá các bài học và hoạt động phù hợp.
                        </p>
                    </div>
                    <div className="mt-8 space-y-4">
                        <RoleCard
                            icon={<UserCircleIcon className="h-7 w-7 text-teal-600" />}
                            title="Tôi là Học sinh (gợi ý)"
                            description="Khám phá các bài học thú vị, chơi trò chơi giáo dục và tự mình học hỏi."
                            onClick={() => handleSelectRole('student')}
                        />
                        <p className="text-center text-sm text-gray-500">Hoặc chọn vai trò khác:</p>
                        <div className="grid grid-cols-2 gap-4">
                            <RoleCard
                                icon={<AcademicCapIcon className="h-7 w-7 text-teal-600" />}
                                title="Tôi là Giáo viên"
                                description="Tạo giáo án, bài kiểm tra với AI."
                                onClick={() => handleSelectRole('teacher')}
                            />
                            <RoleCard
                                icon={<UserGroupIcon className="h-7 w-7 text-teal-600" />}
                                title="Tôi là Phụ huynh"
                                description="Cùng con học tập, theo dõi tiến độ."
                                onClick={() => handleSelectRole('parent')}
                            />
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex justify-center items-center py-16">
            <Card className="max-w-2xl w-full p-10">
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900">Chào mừng bạn đến với Edu App!</h2>
                    <p className="mt-3 text-lg text-gray-600">
                        Bạn đã đủ 18 tuổi. Vui lòng chọn vai trò phù hợp với bạn:
                    </p>
                </div>
                <div className="mt-10 space-y-6">
                    <RoleCard
                        icon={<AcademicCapIcon className="h-7 w-7 text-teal-600" />}
                        title="Tôi là Giáo viên"
                        description="Tạo giáo án, bài kiểm tra, và các tài liệu giảng dạy khác với sự hỗ trợ của AI."
                        onClick={() => handleSelectRole('teacher')}
                    />
                    <RoleCard
                        icon={<UserGroupIcon className="h-7 w-7 text-teal-600" />}
                        title="Tôi là Phụ huynh"
                        description="Cùng con học tập, theo dõi tiến độ và khám phá các hoạt động bổ ích tại nhà."
                        onClick={() => handleSelectRole('parent')}
                    />
                </div>
            </Card>
        </div>
    );
};

export default RoleSelection;
